/**
 * Privacy Policy Page
 * Compliant with Google API Services User Data Policy and COPPA
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  FamilyRestroom,
  Security,
  Visibility,
  Shield,
  Lock,
  Public
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function PrivacyPolicy() {
  const navigate = useNavigate();
  useAuth();

  const lastUpdated = "February 8, 2026";

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            sx={{ 
              minWidth: 48, 
              minHeight: 48, 
              borderRadius: 1,
              padding: '12px',
              marginRight: '8px',
              '& .MuiSvgIcon-root': {
                fontSize: '24px'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <FamilyRestroom sx={{ ml: 1, mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Privacy Policy
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ 
        py: 4,
        '& .MuiTypography-body1': { 
          fontSize: { xs: '16px', sm: '1rem' }, 
          lineHeight: '1.6em',
          marginBottom: '1.5em'
        },
        '& .MuiTypography-body2': { 
          fontSize: { xs: '14px', sm: '0.875rem' }, 
          lineHeight: '1.5em',
          marginBottom: '1em'
        },
        '& .MuiTypography-h6': { 
          fontSize: { xs: '1.1rem', sm: '1.2rem' },
          marginTop: '1.5em',
          marginBottom: '0.75em'
        },
        '& .MuiTypography-h5': { 
          fontSize: { xs: '1.3rem', sm: '1.5rem' },
          marginTop: '2em',
          marginBottom: '1em'
        },
        '& p': {
          marginBottom: '1.5em'
        }
      }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
            <Shield sx={{ mr: 2, verticalAlign: 'middle' }} />
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Last updated: {lastUpdated}
          </Typography>

          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Earn-E Chore Helper ("we," "our," or "us") is committed to protecting your privacy and the privacy of your children. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our family 
              management application and related services.
            </Typography>
            <Typography variant="body1" paragraph>
              This policy complies with the Google API Services User Data Policy, the Children's Online Privacy Protection Act (COPPA), 
              and other applicable privacy laws.
            </Typography>
          </Box>

          {/* Information We Collect */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Visibility sx={{ mr: 1 }} />
              2. Information We Collect
            </Typography>
            
            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.1 Account Information
            </Typography>
            <Typography variant="body1" paragraph>
              When you create an account, we collect:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Name and email address</li>
              <li>Family member information (names, roles, allowances)</li>
              <li>Profile pictures and preferences</li>
              <li>Authentication credentials (stored securely)</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.2 Task and Activity Data
            </Typography>
            <Typography variant="body1" paragraph>
              We collect and store:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Chore assignments and completion status</li>
              <li>Task photos (before/after completion)</li>
              <li>Reward and allowance tracking</li>
              <li>Shopping lists and product information</li>
              <li>Recipe collections and cooking data</li>
              <li>Savings goals and progress</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.3 Google Calendar Integration
            </Typography>
            <Typography variant="body1" paragraph>
              If you choose to connect your Google Calendar:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>We request access to create calendar events for tasks</li>
              <li>We do NOT read existing calendar data</li>
              <li>We only create new events related to assigned tasks</li>
              <li>You can revoke access at any time</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.4 Technical Information
            </Typography>
            <Typography variant="body1" paragraph>
              We automatically collect:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Device information and browser type</li>
              <li>IP address and general location</li>
              <li>Usage patterns and app performance data</li>
              <li>Error logs and crash reports</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              2.5 Analytics Data
            </Typography>
            <Typography variant="body1" paragraph>
              We use Brainstorm AI Kit analytics to understand how visitors use our site. This service collects 
              anonymized data including:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Pages visited and time spent on each page</li>
              <li>Scroll depth and user interactions</li>
              <li>Browser type and device type</li>
              <li>Approximate location (city-level only)</li>
              <li>Page performance metrics</li>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              <strong>Privacy protections:</strong> IP addresses are anonymized before storage. No precise 
              geolocation or personal identification is collected unless you voluntarily provide it through 
              forms or account creation. For more information about how this data is processed, visit the{' '}
              <a href="https://brainstormaikit.com/privacy" target="_blank" rel="noopener noreferrer">
                Brainstorm AI Kit Privacy Policy
              </a>.
            </Typography>
          </Box>

          {/* How We Use Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Lock sx={{ mr: 1 }} />
              3. How We Use Your Information
            </Typography>
            
            <Typography variant="body1" paragraph>
              We use the collected information to:
            </Typography>
            
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Provide and maintain our family management services</li>
              <li>Create and manage task assignments and rewards</li>
              <li>Generate calendar events when you choose to connect Google Calendar</li>
              <li>Process payments through Stripe (for premium features)</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Improve our app's functionality and user experience</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </Box>

            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              <strong>We do NOT:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Sell or rent your personal information to third parties</li>
              <li>Use your data for advertising or marketing purposes</li>
              <li>Allow humans to read your personal data without explicit consent</li>
              <li>Share data with advertising platforms or data brokers</li>
            </Box>
          </Box>

          {/* Google API Services Compliance */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Public sx={{ mr: 1 }} />
              4. Google API Services Compliance
            </Typography>
            
            <Typography variant="body1" paragraph>
              Our use of Google API Services is limited and transparent:
            </Typography>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              4.1 Data Access
            </Typography>
            <Typography variant="body1" paragraph>
              We follow these principles:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>We only request calendar permissions to create task-related events</li>
              <li>We do NOT read, access, or store existing calendar data</li>
              <li>We do NOT access any other Google services or data</li>
              <li>All Google API access is clearly disclosed and user-authorized</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              4.2 Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement these security measures:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Google API credentials are stored securely and kept confidential</li>
              <li>All data transmission is encrypted in transit and at rest</li>
              <li>We follow Google's security best practices</li>
              <li>Regular security assessments and monitoring</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              4.3 User Control
            </Typography>
            <Typography variant="body1" paragraph>
              We ensure user control:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Users can revoke Google Calendar access at any time</li>
              <li>Clear consent is obtained before accessing any Google services</li>
              <li>Users are informed of what data is accessed and why</li>
              <li>No surprises or hidden data access</li>
            </Box>
          </Box>

          {/* COPPA Compliance */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <FamilyRestroom sx={{ mr: 1 }} />
              5. Children's Privacy (COPPA Compliance)
            </Typography>
            
            <Typography variant="body1" paragraph>
              Our app is designed for families with children under 13. We are fully compliant with COPPA:
            </Typography>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              5.1 Parental Consent
            </Typography>
            <Typography variant="body1" paragraph>
              We require parental oversight:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Parents must create accounts and manage family settings</li>
              <li>Children cannot create accounts independently</li>
              <li>All child data is controlled and monitored by parents</li>
              <li>Clear parental consent is required for all data collection</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              5.2 Child Data Protection
            </Typography>
            <Typography variant="body1" paragraph>
              We protect children's data:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Children's personal information is never shared with third parties</li>
              <li>No advertising or marketing to children</li>
              <li>Limited data collection - only what's necessary for app functionality</li>
              <li>Parents can view, modify, or delete their children's data at any time</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              5.3 Google Services and Children
            </Typography>
            <Typography variant="body1" paragraph>
              We restrict Google services for children:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Children under 13 cannot connect Google accounts</li>
              <li>Google Calendar integration is only available to parents</li>
              <li>No Google Sign-In for child accounts</li>
              <li>All Google API access is parent-controlled</li>
            </Box>
          </Box>

          {/* Data Sharing */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              6. Information Sharing and Disclosure
            </Typography>
            
            <Typography variant="body1" paragraph>
              We do not sell, trade, or rent your personal information. We may share information only in these limited circumstances:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Service Providers:</strong> Trusted third parties who assist in app operations (Firebase, Stripe)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Safety:</strong> To prevent fraud or protect user safety</li>
              <li><strong>Consent:</strong> When you explicitly authorize sharing</li>
            </Box>
          </Box>

          {/* Data Security */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              7. Data Security
            </Typography>
            
            <Typography variant="body1" paragraph>
              We implement industry-standard security measures:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and authorization</li>
              <li>Regular security assessments and updates</li>
              <li>Limited access to personal data</li>
              <li>Secure data centers and infrastructure</li>
            </Box>
          </Box>

          {/* Your Rights */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              8. Your Rights and Choices
            </Typography>
            
            <Typography variant="body1" paragraph>
              You have the right to:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Access and review your personal information</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Delete your account and associated data</li>
              <li>Revoke Google Calendar access at any time</li>
              <li>Opt out of data collection (may limit app functionality)</li>
              <li>Request data portability</li>
            </Box>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              9. Contact Us
            </Typography>
            
            <Typography variant="body1" paragraph>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </Typography>

            <Typography variant="body1">
              <strong>Email:</strong> privacy@earn-echore.app<br />
              <strong>Address:</strong> Earn-E Chore Helper Privacy Team<br />
              <strong>Response Time:</strong> We will respond within 30 days
            </Typography>
          </Box>

          {/* Changes to Policy */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              10. Changes to This Privacy Policy
            </Typography>
            
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Posting the new policy on our website</li>
              <li>Sending email notifications to account holders</li>
              <li>Requiring re-consent for Google API access if needed</li>
            </Box>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="body2" align="center">
              <strong>This Privacy Policy is effective as of {lastUpdated}</strong>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;











